import { getPillarDisplayName } from '../constants/awardCategoryPillar';
import { apiRequest } from './apiConfig';
import { getJudgingDataForCategory } from './vistaJudging';

const currentYear = new Date().getFullYear();

/**
 * Fetches award-assignments for the given award role documentIds and returns
 * the set of award_submission documentIds that are explicitly assigned.
 *
 * Only populates the submission's documentId to minimise data exposure.
 *
 * @param roleDocumentIds - Array of award-role documentId values from AuthContext
 * @returns Set of award_submission documentId strings
 */
export const getAssignedSubmissionIds = async (
  roleDocumentIds: string[]
): Promise<Set<string>> => {
  if (!roleDocumentIds.length) return new Set();

  const roleFilters = roleDocumentIds
    .map(
      (id, i) =>
        `filters[award_role][documentId][$in][${i}]=${encodeURIComponent(id)}`
    )
    .join('&');

  const allData: any[] = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100;

  while (hasMorePages) {
    const response = await apiRequest(
      `/award-assignments?${roleFilters}&populate[award_submissions][fields][0]=documentId&pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true`,
      { method: 'GET' }
    );

    const result = await response.json();
    const pageData = result.data || [];
    allData.push(...pageData);

    const pagination = result.meta?.pagination;
    hasMorePages = pagination ? page < pagination.pageCount : false;
    page++;
  }

  const submissionIds = new Set<string>();
  for (const item of allData) {
    const submissions = item.award_submissions;
    if (Array.isArray(submissions)) {
      for (const sub of submissions) {
        if (sub?.documentId) {
          submissionIds.add(sub.documentId);
        }
      }
    }
  }

  return submissionIds;
};

export const getQualificationSubmissions = async (
  year: number
): Promise<{ data: any[] }> => {
  const startDate = `${year}-01-01T00:00:00.000Z`;
  const endDate = `${year}-12-31T23:59:59.999Z`;
  const yearFilter =
    `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}` +
    `&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;

  let allData = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100; // Maximum recommended page size

  while (hasMorePages) {
    const response = await apiRequest(
      `/award-submissions?filters[competition_stage][$in][0]=qualification&filters[competition_stage][$in][1]=final&populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true${yearFilter}`,
      {
        method: 'GET',
      }
    );

    const result = await response.json();

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error(
        `Invalid award-submissions data received from API (page ${page})`
      );
    }

    // Add current page data to total
    allData = allData.concat(result.data);

    // Check pagination metadata for more pages
    const pagination = result.meta?.pagination;
    if (pagination) {
      hasMorePages = page < pagination.pageCount;
    } else {
      hasMorePages = false;
    }

    page++;
  }

  const result = { data: allData };

  // Transform pillar names to display names
  if (result.data && Array.isArray(result.data)) {
    result.data = result.data.map(item => ({
      ...item,
      pillar: getPillarDisplayName(item.pillar),
    }));
  }

  return result;
};

/**
 * Submits an evaluation for a qualification submission using vista-judgment endpoint.
 * This follows the same pattern as SMDDForm but for manual human scoring.
 *
 * @param {object} evaluationData - The evaluation data containing submissionId and scores.
 * @param {object} judgeInfo - Judge information from auth context: { name, email }
 * @returns {Promise<object>} The response from the vista-judgment API.
 */
export const submitQualificationEvaluation = async (
  evaluationData,
  judgeInfo
) => {
  try {
    // Validate judge information
    if (!judgeInfo || !judgeInfo.email) {
      throw new Error('Judge information is required but was not provided');
    }

    // 1. First, fetch the submission to get its category and details
    const submissionResponse = await apiRequest(
      `/award-submissions/${evaluationData.submissionId}`,
      {
        method: 'GET',
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionResult = await submissionResponse.json();
    const submission = submissionResult.data;

    if (!submission) {
      throw new Error('Submission not found');
    }

    // 2. Get judging data for the submission's category (rubric and aspects)
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      currentYear
    );

    if (!judgingData || !judgingData.rubric || !judgingData.aspects) {
      throw new Error(
        `No judging data found for category: ${submission.category}`
      );
    }

    // 4. Map the manual scores to the rubric aspects
    const aspectScores = [];

    // Map scores to aspects using the criteria order
    Object.entries(evaluationData.scores).forEach(([criteriaKey, score]) => {
      // Extract the criteria index (criteria1 -> 0, criteria2 -> 1, etc.)
      const criteriaMatch = criteriaKey.match(/criteria(\d+)/);
      if (!criteriaMatch) {
        console.warn(`⚠️ Invalid criteria key format: ${criteriaKey}`);
        return;
      }

      const criteriaIndex = parseInt(criteriaMatch[1]) - 1; // Convert to 0-based index
      const aspect = judgingData.aspects[criteriaIndex];

      if (aspect) {
        const aspectScore = {
          aspect_id: aspect.documentId,
          value: score,
          comment: `Manual qualification evaluation score for ${submission.category} - ${aspect.name}: ${score}/10`,
        };
        aspectScores.push(aspectScore);
      }
    });

    if (aspectScores.length === 0) {
      throw new Error('No valid aspect scores could be mapped to the rubric');
    }

    // 5. Calculate weighted total score using rubric weights
    let totalScore = 0;
    let totalWeight = 0;

    aspectScores.forEach(aspectScore => {
      const aspect = judgingData.aspects.find(
        a => a.documentId === aspectScore.aspect_id
      );
      if (aspect && aspect.weight) {
        // Use the actual rubric weights
        totalScore += aspectScore.value * (aspect.weight / 100); // Convert percentage to decimal
        totalWeight += aspect.weight / 100;
      } else {
        // Equal weight fallback
        totalScore += aspectScore.value;
        totalWeight += 1;
      }
    });

    // Calculate final weighted average
    const averageScore =
      totalWeight > 0
        ? totalScore / totalWeight
        : totalScore / aspectScores.length;

    // 6. Prepare the judgment data for submission (matching vistaJudging.js structure)
    const judgmentData = {
      submission_document_id: submission.documentId,
      submission_table: 'award-submission',
      rubric_document_id: judgingData.rubric.documentId,
      aspect_scores: aspectScores,
      overall_summary: 'Completed',
      judge_name: judgeInfo.name,
      judge_email: judgeInfo.email,
      judged_at: new Date().toISOString(),
    };

    console.log('📊 Step 3 Data - Judgment Payload:', {
      submission_document_id: judgmentData.submission_document_id,
      submission_table: judgmentData.submission_table,
      rubric_document_id: judgmentData.rubric_document_id,
      judge_name: judgmentData.judge_name,
      judge_email: judgmentData.judge_email,
      judged_at: judgmentData.judged_at,
      overall_summary: judgmentData.overall_summary,
      aspect_scores: judgmentData.aspect_scores,
      fullPayload: { data: judgmentData },
    });

    // Submit to vista-judgments endpoint
    const response = await apiRequest('/vista-judgments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: judgmentData }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Vista-judgments submission failed: ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();

    return {
      success: true,
      submission: submission,
      judgment: result,
      totalScore: averageScore,
      judgeInfo: judgeInfo,
    };
  } catch (error) {
    console.error('❌ Error in submitQualificationEvaluation:', {
      error: error.message,
      stack: error.stack,
      evaluationData,
      judgeInfo,
    });

    return {
      success: false,
      error: error.message,
      evaluationData,
      judgeInfo,
    };
  }
};

/**
 * Checks if a judge has already evaluated a specific submission
 * @param {string} submissionId - The submission document ID
 * @param {string} judgeEmail - The judge's email address
 * @returns {Promise<boolean>} - True if already evaluated, false otherwise
 */
export const hasJudgeEvaluatedSubmission = async (submissionId, judgeEmail) => {
  try {
    console.log('🔍 Checking if judge has evaluated submission:', {
      submissionId,
      judgeEmail,
    });

    // Query vista-judgments for this submission and judge
    const response = await apiRequest(
      `/vista-judgments?filters[submission_document_id][$eq]=${submissionId}&filters[judge_email][$eq]=${judgeEmail}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      console.warn('⚠️ Failed to check judgment history, allowing evaluation');
      return false; // If we can't check, allow the evaluation
    }

    const result = await response.json();
    const existingJudgments = result.data || [];

    const hasEvaluated = existingJudgments.length > 0;
    console.log(
      `${hasEvaluated ? '❌' : '✅'} Judge ${judgeEmail} ${hasEvaluated ? 'has already' : 'has not'} evaluated submission ${submissionId}`
    );

    return hasEvaluated;
  } catch (error) {
    console.error('❌ Error checking evaluation history:', error);
    return false; // If error occurs, allow the evaluation
  }
};

/**
 * Efficiently filters out submissions that have already been evaluated by the current judge
 * Uses a single API call to get all judgments, then filters locally for better performance
 * @param {Array} submissions - Array of submission objects
 * @param {string} judgeEmail - The judge's email address
 * @returns {Promise<Array>} - Filtered array of submissions
 */
/**
 * Debug function to fetch and examine vista-judgments data
 */
export const debugVistaJudgments = async judgeEmail => {
  console.log('🔍 === DEBUGGING VISTA-JUDGMENTS ===');
  console.log('🔍 Judge Email:', judgeEmail);

  try {
    // Step 1: Fetch ALL vista-judgments (with complete pagination)
    console.log('🔍 Step 1: Fetching ALL vista-judgments...');
    let allJudgments = [];
    let page = 1;
    let hasMorePages = true;
    const pageSize = 100; // Maximum recommended page size

    while (hasMorePages) {
      const response = await apiRequest(
        `/vista-judgments?pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true`,
        { method: 'GET' }
      );

      if (!response.ok) {
        console.error(
          '❌ Failed to fetch vista-judgments:',
          response.status,
          response.statusText
        );
        return null;
      }

      const result = await response.json();
      const pageData = result.data || [];

      // Add current page data to total
      allJudgments = allJudgments.concat(pageData);

      // Check pagination metadata for more pages
      const pagination = result.meta?.pagination;
      if (pagination) {
        hasMorePages = page < pagination.pageCount;

        // Log progress for large datasets
        if (pagination.pageCount > 1) {
          console.log(
            `📄 Fetched vista-judgments page ${page}/${pagination.pageCount} (${pageData.length} items, ${allJudgments.length}/${pagination.total} total)`
          );
        }
      } else {
        hasMorePages = false;
      }

      page++;
    }

    console.log('🔍 Step 1 RESULT: ALL VISTA-JUDGMENTS');
    console.log('📊 Total judgments:', allJudgments.length);
    console.log('📊 All judgments data:', allJudgments);

    // Step 2: Filter by judge email
    console.log('🔍 Step 2: Filtering by judge email...');
    const judgeJudgments = allJudgments.filter(
      judgment => judgment.judge_email === judgeEmail
    );

    console.log('🔍 Step 2 RESULT: FILTERED BY JUDGE EMAIL');
    console.log('📊 Judge email:', judgeEmail);
    console.log('📊 Judge judgments count:', judgeJudgments.length);
    console.log('📊 Judge judgments data:', judgeJudgments);

    // Step 3: Extract submission IDs
    console.log('🔍 Step 3: Extracting submission document IDs...');
    const submissionIds = judgeJudgments.map(
      judgment => judgment.submission_document_id
    );

    console.log('🔍 Step 3 RESULT: EXTRACTED SUBMISSION IDs');
    console.log('📊 Submission document IDs:', submissionIds);
    console.log('📊 Unique submission IDs:', [...new Set(submissionIds)]);

    console.log('🔍 === END DEBUGGING ===');

    return {
      allJudgments,
      judgeJudgments,
      submissionIds: [...new Set(submissionIds)],
    };
  } catch (error) {
    console.error('❌ Error in debugging vista-judgments:', error);
    return null;
  }
};

export const filterUnevaluatedSubmissions = async (submissions, judgeEmail) => {
  if (!judgeEmail || !Array.isArray(submissions) || submissions.length === 0) {
    return submissions;
  }

  try {
    // Debug vista-judgments data first
    const debugData = await debugVistaJudgments(judgeEmail);

    if (!debugData) {
      console.log(
        '⚠️ Failed to fetch vista-judgments, showing all submissions'
      );
      return submissions;
    }

    // Create Set of evaluated submission IDs
    const evaluatedSubmissionIds = new Set(debugData.submissionIds);

    // Filter submissions
    const unevaluatedSubmissions = submissions.filter(submission => {
      const submissionId = submission.documentId || submission.id;
      const isEvaluated = evaluatedSubmissionIds.has(submissionId);

      console.log('🔍 Checking submission:', {
        submissionId,
        isEvaluated,
        evaluatedIds: debugData.submissionIds,
      });

      return !isEvaluated;
    });

    console.log('🔍 FINAL FILTERING RESULT:', {
      originalCount: submissions.length,
      evaluatedCount: submissions.length - unevaluatedSubmissions.length,
      unevaluatedCount: unevaluatedSubmissions.length,
      unevaluatedIds: unevaluatedSubmissions.map(s => s.documentId || s.id),
    });

    return unevaluatedSubmissions;
  } catch (error) {
    console.error('❌ Error in filtering:', error);
    return submissions; // If error occurs, allow all submissions
  }
};

export const getAIJudgment = async (submissionDocId: string) => {
  if (!submissionDocId) return null;
  try {
    const response = await apiRequest(
      `/vista-judgments?filters[submission_document_id][$eq]=${submissionDocId}&filters[judge_name][$eq]=AI%20Judge`,
      { method: 'GET' }
    );
    if (!response.ok) return null;
    const result = await response.json();
    const judgments = result.data || [];
    return judgments.length > 0 ? judgments[0] : null;
  } catch {
    return null;
  }
};

/**
 * Gets existing judgments for a judge for specific submissions
 * @param {Array} submissions - Array of submission objects
 * @param {string} judgeEmail - The judge's email address
 * @returns {Promise<Object>} - Object mapping submission IDs to judgment data
 */
export const getExistingJudgments = async (submissions, judgeEmail) => {
  if (!judgeEmail || !Array.isArray(submissions) || submissions.length === 0) {
    return {};
  }

  try {
    console.log('🔍 Fetching existing judgments for judge:', judgeEmail);

    // Get all judgments for this judge
    let allJudgments = [];
    let page = 1;
    let hasMorePages = true;
    const pageSize = 100;

    while (hasMorePages) {
      const response = await apiRequest(
        `/vista-judgments?filters[judge_email][$eq]=${judgeEmail}&pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true`,
        { method: 'GET' }
      );

      if (!response.ok) {
        console.warn('⚠️ Failed to fetch existing judgments');
        return {};
      }

      const result = await response.json();
      const pageData = result.data || [];

      allJudgments = allJudgments.concat(pageData);

      const pagination = result.meta?.pagination;
      if (pagination) {
        hasMorePages = page < pagination.pageCount;
      } else {
        hasMorePages = false;
      }

      page++;
    }

    // Create mapping of submission ID to judgment data
    const judgmentMap = {};
    const submissionIds = submissions.map(s => s.documentId || s.id);

    allJudgments.forEach(judgment => {
      if (submissionIds.includes(judgment.submission_document_id)) {
        judgmentMap[judgment.submission_document_id] = judgment;
      }
    });

    return judgmentMap;
  } catch (error) {
    console.error('❌ Error fetching existing judgments:', error);
    return {};
  }
};

/**
 * Filters out submissions that have been completed (overall_summary = "Completed")
 * Only shows submissions that are either not evaluated or still in draft status
 * @param {Array} submissions - Array of submission objects
 * @param {Object} existingJudgments - Object mapping submission IDs to judgment data
 * @returns {Array} - Filtered array of submissions (excludes completed ones)
 */
export const filterIncompleteSubmissions = (submissions, existingJudgments) => {
  if (!Array.isArray(submissions) || submissions.length === 0) {
    return submissions;
  }

  const filteredSubmissions = submissions.filter(submission => {
    const submissionId = submission.documentId || submission.id;
    const judgment = existingJudgments[submissionId];

    // Include submission if:
    // 1. No judgment exists (not started)
    // 2. Judgment exists but overall_summary is "Draft" (can be revised)
    // Exclude if overall_summary indicates completion
    if (!judgment) {
      return true; // Not started - include
    }

    if (judgment.overall_summary === 'Draft') {
      return true; // Draft - include for revision
    }

    // If overall_summary contains completion indicators, exclude
    const isCompleted =
      judgment.overall_summary &&
      (judgment.overall_summary.includes('completed') ||
        judgment.overall_summary.includes('Completed') ||
        judgment.overall_summary !== 'Draft');

    return !isCompleted; // Exclude completed submissions
  });

  console.log('📊 Submission Filtering Results:', {
    originalCount: submissions.length,
    filteredCount: filteredSubmissions.length,
    excludedCount: submissions.length - filteredSubmissions.length,
    excludedSubmissions: submissions
      .filter(s => !filteredSubmissions.includes(s))
      .map(s => ({
        id: s.documentId || s.id,
        projectName: s.project_name,
        judgmentStatus:
          existingJudgments[s.documentId || s.id]?.overall_summary,
      })),
  });

  return filteredSubmissions;
};

/**
 * Updates an existing qualification evaluation using PUT
 * @param {string} judgmentId - The judgment document ID to update
 * @param {object} evaluationData - The evaluation data containing submissionId and scores
 * @param {object} judgeInfo - Judge information from auth context: { name, email }
 * @param {boolean} isDraft - Whether this is a draft (true) or final submission (false)
 * @returns {Promise<object>} The response from the vista-judgment API
 */
export const updateQualificationEvaluation = async (
  judgmentId,
  evaluationData,
  judgeInfo,
  isDraft = true
) => {
  try {
    console.log('🔄 UPDATING EXISTING EVALUATION');
    console.log('📋 Input Parameters:', {
      judgmentId,
      submissionId: evaluationData.submissionId,
      judgeEmail: judgeInfo?.email,
      isDraft,
      scoresCount: Object.keys(evaluationData.scores || {}).length,
    });

    // Validate judge information
    if (!judgeInfo || !judgeInfo.email) {
      throw new Error('Judge information is required but was not provided');
    }

    // 1. First, GET the existing judgment to preserve all data
    console.log('📥 STEP 1: Fetching existing judgment data...');
    const getResponse = await apiRequest(`/vista-judgments/${judgmentId}`, {
      method: 'GET',
    });

    if (!getResponse.ok) {
      throw new Error(
        `Failed to fetch existing judgment ${judgmentId}: ${getResponse.statusText}`
      );
    }

    const existingJudgment = await getResponse.json();
    const existingData = existingJudgment.data;

    if (!existingData) {
      throw new Error(`Judgment ${judgmentId} not found`);
    }

    console.log('✅ STEP 1 Complete: Retrieved existing judgment data:', {
      judgmentId,
      documentId: existingData.documentId,
      currentAspectScoresCount: existingData.aspect_scores?.length || 0,
      currentSummary: existingData.overall_summary,
    });

    // Extract documentId for reference (we'll only preserve this)
    const documentId = existingData.documentId;

    // 2. Fetch the submission to get its category and details
    console.log('📄 STEP 2: Fetching submission details...');
    const submissionResponse = await apiRequest(
      `/award-submissions/${evaluationData.submissionId}`,
      {
        method: 'GET',
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionResult = await submissionResponse.json();
    const submission = submissionResult.data;

    if (!submission) {
      throw new Error('Submission not found');
    }

    // 3. Get judging data for the submission's category (rubric and aspects)
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      currentYear
    );

    if (!judgingData || !judgingData.rubric || !judgingData.aspects) {
      throw new Error(
        `No judging data found for category: ${submission.category}`
      );
    }

    console.log('✅ STEP 3 Complete: Retrieved judging data:', {
      category: submission.category,
      rubricId: judgingData.rubric.documentId,
      aspectsCount: judgingData.aspects.length,
      aspects: judgingData.aspects.map(a => ({
        id: a.documentId,
        name: a.name,
        weight: a.weight,
      })),
    });

    // 4. Map the manual scores to the rubric aspects
    console.log('🎯 STEP 4: Mapping scores to rubric aspects...');
    const aspectScores = [];

    Object.entries(evaluationData.scores).forEach(([criteriaKey, score]) => {
      const criteriaMatch = criteriaKey.match(/criteria(\d+)/);
      if (!criteriaMatch) {
        console.warn(`⚠️ Invalid criteria key format: ${criteriaKey}`);
        return;
      }

      const criteriaIndex = parseInt(criteriaMatch[1]) - 1;
      const aspect = judgingData.aspects[criteriaIndex];

      if (aspect) {
        const aspectScore = {
          aspect_id: aspect.documentId,
          value: score,
          comment: `Manual qualification evaluation score for ${submission.category} - ${aspect.name}: ${score}/10`,
        };
        aspectScores.push(aspectScore);
        console.log(
          `   ✓ Mapped ${criteriaKey} (${score}) → ${aspect.name} (${aspect.documentId})`
        );
      }
    });

    if (aspectScores.length === 0) {
      throw new Error('No valid aspect scores could be mapped to the rubric');
    }

    console.log('✅ STEP 4 Complete: Score mapping successful:', {
      inputScores: evaluationData.scores,
      mappedAspectScores: aspectScores.length,
      aspectScores: aspectScores.map(s => ({
        aspect_id: s.aspect_id,
        value: s.value,
      })),
    });

    // 5. Calculate weighted total score using rubric weights
    console.log('🧮 STEP 5: Calculating weighted scores...');
    let totalScore = 0;
    let totalWeight = 0;

    aspectScores.forEach(aspectScore => {
      const aspect = judgingData.aspects.find(
        a => a.documentId === aspectScore.aspect_id
      );
      if (aspect && aspect.weight) {
        totalScore += aspectScore.value * (aspect.weight / 100);
        totalWeight += aspect.weight / 100;
        console.log(
          `   ✓ ${aspect.name}: ${aspectScore.value} × ${aspect.weight}% = ${(aspectScore.value * (aspect.weight / 100)).toFixed(2)}`
        );
      } else {
        totalScore += aspectScore.value;
        totalWeight += 1;
        console.log(
          `   ✓ ${aspect?.name || 'Unknown'}: ${aspectScore.value} × 1 (equal weight) = ${aspectScore.value}`
        );
      }
    });

    const averageScore =
      totalWeight > 0
        ? totalScore / totalWeight
        : totalScore / aspectScores.length;

    console.log('✅ STEP 5 Complete: Score calculation:', {
      totalScore: totalScore.toFixed(2),
      totalWeight: totalWeight.toFixed(2),
      averageScore: averageScore.toFixed(2),
    });

    const overallSummary = isDraft ? 'Draft' : 'Completed';

    // 6. Prepare minimal data for PUT request (only send what we want to update)
    console.log('🔄 STEP 6: Preparing minimal data for PUT request...');
    const updatedData = {
      aspect_scores: aspectScores, // Update the aspect scores
      overall_summary: overallSummary, // Update summary based on draft status
      judged_at: new Date().toISOString(), // Update timestamp
    };

    console.log('✅ STEP 6 Complete: Minimal data preparation for PUT:', {
      judgmentId,
      documentId, // Reference only, not sent in PUT
      isDraft,
      overallSummary,
      fieldsToUpdate: Object.keys(updatedData),
      aspect_scores: updatedData.aspect_scores,
      dataComparison: {
        before: {
          aspectScoresCount: existingData.aspect_scores?.length || 0,
          summary: existingData.overall_summary,
        },
        after: {
          aspectScoresCount: updatedData.aspect_scores.length,
          summary: updatedData.overall_summary,
        },
      },
    });

    // 7. Update existing judgment using PUT with minimal data
    console.log('📤 STEP 7: Sending minimal PUT request to update judgment...');
    console.log('📋 PUT Request Body:', { data: updatedData });

    const response = await apiRequest(`/vista-judgments/${judgmentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: updatedData }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ PUT request failed:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        judgmentId,
        requestBody: { data: updatedData },
      });
      throw new Error(
        `Vista-judgments update failed: ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();

    console.log('✅ STEP 7 Complete: Minimal PUT request successful:', {
      judgmentId,
      documentId,
      responseStatus: response.status,
      updatedJudgment: result.data?.documentId || 'N/A',
    });

    console.log('🎉 UPDATE EVALUATION COMPLETED SUCCESSFULLY:', {
      judgmentId,
      submissionId: evaluationData.submissionId,
      category: submission.category,
      averageScore: averageScore.toFixed(2),
      isDraft,
      judgeName: judgeInfo.name,
      aspectScoresUpdated: aspectScores.length,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      submission: submission,
      judgment: result,
      totalScore: averageScore,
      judgeInfo: judgeInfo,
      isDraft,
    };
  } catch (error) {
    console.error('❌ ERROR IN UPDATE QUALIFICATION EVALUATION:', {
      error: error.message,
      stack: error.stack,
      judgmentId,
      submissionId: evaluationData?.submissionId,
      judgeEmail: judgeInfo?.email,
      isDraft,
      inputScores: evaluationData?.scores,
      timestamp: new Date().toISOString(),
    });

    return {
      success: false,
      error: error.message,
      judgmentId,
      evaluationData,
      judgeInfo,
      isDraft,
    };
  }
};

/**
 * Submits an evaluation for a qualification submission as a draft initially
 * Modified version that sets overall_summary to "Draft"
 */
export const submitQualificationEvaluationAsDraft = async (
  evaluationData,
  judgeInfo
) => {
  try {
    // Validate judge information
    if (!judgeInfo || !judgeInfo.email) {
      throw new Error('Judge information is required but was not provided');
    }

    // 1. First, fetch the submission to get its category and details
    const submissionResponse = await apiRequest(
      `/award-submissions/${evaluationData.submissionId}`,
      {
        method: 'GET',
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionResult = await submissionResponse.json();
    const submission = submissionResult.data;

    if (!submission) {
      throw new Error('Submission not found');
    }

    // 2. Get judging data for the submission's category (rubric and aspects)
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      currentYear
    );

    if (!judgingData || !judgingData.rubric || !judgingData.aspects) {
      throw new Error(
        `No judging data found for category: ${submission.category}`
      );
    }

    // 4. Map the manual scores to the rubric aspects
    const aspectScores = [];

    // Map scores to aspects using the criteria order
    Object.entries(evaluationData.scores).forEach(([criteriaKey, score]) => {
      // Extract the criteria index (criteria1 -> 0, criteria2 -> 1, etc.)
      const criteriaMatch = criteriaKey.match(/criteria(\d+)/);
      if (!criteriaMatch) {
        console.warn(`⚠️ Invalid criteria key format: ${criteriaKey}`);
        return;
      }

      const criteriaIndex = parseInt(criteriaMatch[1]) - 1; // Convert to 0-based index
      const aspect = judgingData.aspects[criteriaIndex];

      if (aspect) {
        const aspectScore = {
          aspect_id: aspect.documentId,
          value: score,
          comment: `Manual qualification evaluation score for ${submission.category} - ${aspect.name}: ${score}/10`,
        };
        aspectScores.push(aspectScore);
      }
    });

    if (aspectScores.length === 0) {
      throw new Error('No valid aspect scores could be mapped to the rubric');
    }

    // 5. Calculate weighted total score using rubric weights
    let totalScore = 0;
    let totalWeight = 0;

    aspectScores.forEach(aspectScore => {
      const aspect = judgingData.aspects.find(
        a => a.documentId === aspectScore.aspect_id
      );
      if (aspect && aspect.weight) {
        // Use the actual rubric weights
        totalScore += aspectScore.value * (aspect.weight / 100); // Convert percentage to decimal
        totalWeight += aspect.weight / 100;
      } else {
        // Equal weight fallback
        totalScore += aspectScore.value;
        totalWeight += 1;
      }
    });

    // Calculate final weighted average
    const averageScore =
      totalWeight > 0
        ? totalScore / totalWeight
        : totalScore / aspectScores.length;

    // 6. Prepare the judgment data for submission (with Draft status)
    const judgmentData = {
      submission_document_id: submission.documentId,
      submission_table: 'award-submission',
      rubric_document_id: judgingData.rubric.documentId,
      aspect_scores: aspectScores,
      overall_summary: 'Draft', // Set as Draft initially
      judge_name: judgeInfo.name,
      judge_email: judgeInfo.email,
      judged_at: new Date().toISOString(),
    };

    console.log('📊 DRAFT SUBMISSION Data - Judgment Payload:', {
      submission_document_id: judgmentData.submission_document_id,
      submission_table: judgmentData.submission_table,
      rubric_document_id: judgmentData.rubric_document_id,
      judge_name: judgmentData.judge_name,
      judge_email: judgmentData.judge_email,
      judged_at: judgmentData.judged_at,
      overall_summary: judgmentData.overall_summary,
      aspect_scores: judgmentData.aspect_scores,
      fullPayload: { data: judgmentData },
    });

    // Submit to vista-judgments endpoint
    const response = await apiRequest('/vista-judgments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: judgmentData }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Vista-judgments submission failed: ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();

    return {
      success: true,
      submission: submission,
      judgment: result,
      totalScore: averageScore,
      judgeInfo: judgeInfo,
      isDraft: true,
    };
  } catch (error) {
    console.error('❌ Error in submitQualificationEvaluationAsDraft:', {
      error: error.message,
      stack: error.stack,
      evaluationData,
      judgeInfo,
    });

    return {
      success: false,
      error: error.message,
      evaluationData,
      judgeInfo,
    };
  }
};

export const completeAllEvaluations = async (
  judgmentIds: string[],
  judgeInfo: { name: string; email: string }
) => {
  try {
    if (!judgeInfo || !judgeInfo.email) {
      throw new Error('Judge information is required but was not provided');
    }

    if (!Array.isArray(judgmentIds) || judgmentIds.length === 0) {
      throw new Error('No judgment IDs provided for completion');
    }

    const results = [];
    const errors = [];

    // Process each judgment
    for (let i = 0; i < judgmentIds.length; i++) {
      const judgmentId = judgmentIds[i];
      try {
        // First, get the existing judgment for validation and score calculation
        const getResponse = await apiRequest(`/vista-judgments/${judgmentId}`, {
          method: 'GET',
        });

        if (!getResponse.ok) {
          throw new Error(
            `Failed to fetch judgment ${judgmentId}: ${getResponse.statusText}`
          );
        }

        const existingJudgment = await getResponse.json();
        const judgmentData = existingJudgment.data;

        if (!judgmentData) {
          throw new Error(`Judgment ${judgmentId} not found`);
        }

        // Prepare minimal data - only update overall_summary
        const updatedData = {
          overall_summary: 'Completed',
        };

        const updateResponse = await apiRequest(
          `/vista-judgments/${judgmentId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: updatedData }),
          }
        );

        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error(`❌ PUT Failed for ${judgmentId}:`, {
            status: updateResponse.status,
            statusText: updateResponse.statusText,
            errorText,
            requestBody: { data: updatedData },
          });
          throw new Error(
            `Failed to complete judgment ${judgmentId}: ${updateResponse.statusText} - ${errorText}`
          );
        }

        const result = await updateResponse.json();
        results.push({
          judgmentId,
          success: true,
          data: result,
        });
      } catch (error) {
        console.error(`❌ Error completing judgment ${judgmentId}:`, {
          judgmentId,
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
        });
        errors.push({
          judgmentId,
          error: error.message,
        });
      }
    }

    return {
      success: errors.length === 0,
      results,
      errors,
      totalProcessed: judgmentIds.length,
      successfulCount: results.length,
      errorCount: errors.length,
    };
  } catch (error) {
    console.error('❌ Error in completeAllEvaluations:', error);
    return {
      success: false,
      error: error.message,
      results: [],
      errors: [{ error: error.message }],
      totalProcessed: 0,
      successfulCount: 0,
      errorCount: 1,
    };
  }
};
